/** The catalog list's view as URL search params: parsing, writing back, sorting. */

import type { AssetListParams, AssetSort } from '@/api/types';

export const DEFAULT_PAGE_SIZE = 25;
const SORTS: AssetSort[] = ['relevance', 'name', 'kind', 'updated', 'confirmed'];
/** Words open A to Z; dates and relevance open best or newest first. */
const ASCENDING_FIRST = new Set<AssetSort>(['name', 'kind']);

export interface CatalogView {
  q: string;
  kind: string[];
  status: string[];
  maturity: string[];
  industry: string[];
  partner: string[];
  country: string[];
  stale: boolean | null;
  sort: AssetSort | null;
  order: 'asc' | 'desc' | null;
  page: number;
  size: number;
}

const LISTS = ['kind', 'status', 'maturity', 'industry', 'partner', 'country'] as const;

function staleOf(value: string | null): boolean | null {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

export function parseCatalogView(search: URLSearchParams): CatalogView {
  const sort = search.get('sort') as AssetSort | null;
  const order = search.get('order');
  return {
    q: search.get('q') ?? '',
    kind: search.getAll('kind'),
    status: search.getAll('status'),
    maturity: search.getAll('maturity'),
    industry: search.getAll('industry'),
    partner: search.getAll('partner'),
    country: search.getAll('country'),
    stale: staleOf(search.get('stale')),
    sort: sort && SORTS.includes(sort) ? sort : null,
    order: order === 'asc' || order === 'desc' ? order : null,
    page: Math.max(1, Number(search.get('page')) || 1),
    size: Number(search.get('size')) || DEFAULT_PAGE_SIZE,
  };
}

/** The URL for `view`, defaults left out. */
export function catalogSearch(view: CatalogView): URLSearchParams {
  const query = new URLSearchParams();
  if (view.q) query.set('q', view.q);
  for (const key of LISTS) view[key].forEach((v) => query.append(key, v));
  if (view.stale !== null) query.set('stale', String(view.stale));
  if (view.sort) query.set('sort', view.sort);
  if (view.order) query.set('order', view.order);
  if (view.page > 1) query.set('page', String(view.page));
  if (view.size !== DEFAULT_PAGE_SIZE) query.set('size', String(view.size));
  return query;
}

/** Anything but a page change starts again at page 1. */
export function applyPatch(view: CatalogView, patch: Partial<CatalogView>): CatalogView {
  return { ...view, ...(patch.page === undefined ? { page: 1 } : {}), ...patch };
}

/** The sort the server applies: relevance while searching, else the name. */
export function effectiveSort(view: CatalogView): AssetSort {
  return view.sort ?? (view.q ? 'relevance' : 'name');
}

export function effectiveOrder(view: CatalogView): 'asc' | 'desc' {
  return view.order ?? (ASCENDING_FIRST.has(effectiveSort(view)) ? 'asc' : 'desc');
}

/** The same column flips its order; a new one opens in its natural order. */
export function sortPatch(view: CatalogView, key: string): Partial<CatalogView> {
  const sort = key as AssetSort;
  if (effectiveSort(view) === sort) return { sort, order: effectiveOrder(view) === 'asc' ? 'desc' : 'asc' };
  return { sort, order: ASCENDING_FIRST.has(sort) ? 'asc' : 'desc' };
}

export function isFiltered(view: CatalogView): boolean {
  return view.q !== '' || view.stale !== null || LISTS.some((key) => view[key].length > 0);
}

/** How many filters behind "More filters" are set (its count on narrow screens). */
export function moreFilterCount(view: CatalogView): number {
  return [view.industry, view.partner, view.country].filter((l) => l.length > 0).length + (view.stale === null ? 0 : 1);
}

export function toListParams(view: CatalogView): AssetListParams {
  return {
    q: view.q || undefined,
    kind: view.kind,
    status: view.status,
    maturity: view.maturity,
    industry: view.industry,
    partner: view.partner,
    country: view.country,
    stale: view.stale,
    sort: view.sort ?? undefined,
    order: view.order ?? undefined,
    page: view.page,
    page_size: view.size,
  };
}
